import { useQuery } from "@apollo/client";
import { useFilters } from "../../../../Components/Filters/Filters.hook";
import { QUERY_AI_CITATIONS } from "../../../../GQL/CitationsQuery";

export type CitationMetadata = {
    uuid: string;
    location_title: string;
    location_breadcrumb: string;
    offer_type_title: string;
    category_title?: string;
    bedroom_id?: string;
    title: string;
    url: string;
    location_level: number;
};

export type CitationData = {
    html_description?: string;
    metadata?: CitationMetadata;
};

const useCitationsResults = () => {
    const { filters } = useFilters();

    // Transform filters to match the citation query structure
    const getCitationParams = () => {
        // Only execute if we have some basic filters AND we're on page 1
        if (!filters?.operation_type_id && !filters?.property_type_id) {
            return null;
        }

        // Only show citations on page 1
        const currentPage = filters?.page?.value || filters?.page || 1;
        if (currentPage !== 1) {
            return null;
        }

        // Return null if filters contains any of the specified keys
        const excludedKeys = [
            'publicationDate', 'searchstring', 'seaview', 'owner_id', 'projects',
            'projectTitle', 'withBase', 'auctionType', 'search_by_point_of_interest', 'overID',
            'privateOwner', 'facilitiesGroup', 'security', 'socialHousing', 'garage',
            'hasGarage', 'constStatesID', 'floors', 'dispositionID', 'condominium',
            'minPrice', 'maxPrice', 'm2Min', 'm2Max', 'commonExpenses', 'groundUssageID', 'season',
            'dateFrom', 'dateTo', 'guests', 'bathrooms', 'seaDistanceID',
            'map_bounds', 'map_polygon', 'map_polygons', 'securityId',
            'percentage_sold', 'percentage_finished', 'sponsor', 'architect',
            'builder', 'minM2Price', 'maxM2Price', 'tour3d', 'minOccupationDate',
            'maxOccupationDate', 'seen', 'stratum', 'antiquity'
        ];

        for (const key of excludedKeys) {
            if (filters && filters[key] !== undefined && filters[key] !== null) {
                // If it's an array, only return null if it's not empty
                if (Array.isArray(filters[key])) {
                    if (filters[key].length > 0) {
                        return null;
                    }
                } else {
                    // For non-array values, return null if they have any value
                    return null;
                }
            }
        }

        const params: any = {};

        try {

        // Map operation type
        if (filters.operation_type_id) {
            params.operation_type_id = filters.operation_type_id.value || filters.operation_type_id;
        }

        // Map property type
        if (filters.property_type_id) {
            if (Array.isArray(filters.property_type_id)) {
                params.property_type_id = filters.property_type_id.map((pt: any) => pt?.value || pt);
            } else {
                params.property_type_id = [(filters.property_type_id as any)?.value || filters.property_type_id];
            }
        }


        // Map locations - clean up to only include fields accepted by LocationInput
        if (filters.locations) {
            const cleanLocations = (Array.isArray(filters.locations) 
                ? filters.locations 
                : [filters.locations]
            ).map((location: any) => ({
                id: location.id,
                type: location.type
                // Remove name, estate and other fields not accepted by LocationInput
            }));
            
            if (cleanLocations.length > 0) {
                params.locations = cleanLocations;
            }
        }

        // Map neighborhood/estate to location_slug if available
        if (filters.neighborhood_id && Array.isArray(filters.neighborhood_id) && filters.neighborhood_id.length === 1) {
            const neighborhood = filters.neighborhood_id[0];
            params.location_slug = (neighborhood as any)?.text?.toLowerCase().replace(/\s+/g, '-') || 
                                  (neighborhood as any)?.name?.toLowerCase().replace(/\s+/g, '-');
            
            // Also add to locations array if not already there
            if (!params.locations && (neighborhood as any)?.id) {
                params.locations = [{
                    id: (neighborhood as any).id,
                    type: "NEIGHBOURHOOD" // or NEIGHBORHOOD based on backend expectation
                }];
            }
        } else if (filters.estate_id) {
            params.location_slug = (filters.estate_id as any)?.text?.toLowerCase().replace(/\s+/g, '-') ||
                                  (filters.estate_id as any)?.name?.toLowerCase().replace(/\s+/g, '-');
        }

            return params;
        } catch (error) {
            return null;
        }
    };

    const citationParams = getCitationParams();

    const { data, loading, error } = useQuery(QUERY_AI_CITATIONS, {
        variables: citationParams || {},
        skip: !citationParams || Object.keys(citationParams || {}).length === 0,
        fetchPolicy: "cache-first",
        errorPolicy: "all" // Continue even if there are GraphQL errors
    });

    return {
        loading,
        data: data?.aiCitationSearch as CitationData,
        error,
        hasCitations: !!(data?.aiCitationSearch?.html_description)
    };
};

export { useCitationsResults };
